import { z } from "zod";
import { isAadhaar, isGSTIN, isIFSC, isPAN, normalisePhone, PHONE_RE, PINCODE_RE } from "./validators.js";

/** Reusable Zod building blocks. Money is integer paise. Dates are "YYYY-MM-DD" strings. */
export const zPaise = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const zPositivePaise = zPaise.refine((v) => v > 0, "Amount must be more than zero");
export const zUuid = z.string().uuid();
// Round trip through the calendar: Date.parse("2026-02-31") rolls over to 3 March instead of failing.
const isRealDate = (s: string) => { const d = new Date(`${s}T00:00:00Z`); return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s; };
export const zDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD").refine(isRealDate, "This date does not exist");
export const zPhone = z.string().transform(normalisePhone).refine((v) => PHONE_RE.test(v), "Enter a 10-digit mobile number");
export const zPan = z.string().trim().toUpperCase().refine(isPAN, "PAN must look like ABCDE1234F");
export const zAadhaar = z.string().transform((v) => v.replace(/\s/g, "")).refine(isAadhaar, "Aadhaar number is not valid");
export const zGstin = z.string().trim().toUpperCase().refine(isGSTIN, "GSTIN is not valid");
export const zIfsc = z.string().trim().toUpperCase().refine(isIFSC, "IFSC must look like SBIN0001234");
export const zPincode = z.string().regex(PINCODE_RE, "Enter a 6-digit PIN code");
export const zName = z.string().trim().min(1, "Required").max(200);
export const zText = z.string().trim().max(5000);

export const zPagination = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(50),
});

export const zDateRange = z.object({ from: zDate.optional(), to: zDate.optional() }).refine(
  (r) => !r.from || !r.to || r.from <= r.to,
  "From date must be on or before the To date",
);

export const PAYMENT_MODES = ["cash", "upi", "card", "cheque", "netbanking", "dd"] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];
export const zPaymentMode = z.enum(PAYMENT_MODES);

export const LINE_KINDS = ["ticket", "seva", "donation", "prasad", "room", "hundi", "other"] as const;
export type LineKind = (typeof LINE_KINDS)[number];

/** Standard list response envelope used by every list endpoint. */
export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Standard error envelope returned by the API. */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}
